import type { AnalyticsFilters, AreaLocation } from "@/features/deployment-dashboard/types";
import { ALL_AREAS_FILTER_VALUE } from "@/features/deployment-dashboard/types";

/** Preset date range options for quick selection. */
export const DATE_RANGE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last-7-days", label: "Last 7 Days" },
  { value: "last-30-days", label: "Last 30 Days" },
  { value: "last-90-days", label: "Last 90 Days" },
  { value: "custom", label: "Custom Range" },
] as const;

export type DateRangePreset = (typeof DATE_RANGE_PRESETS)[number]["value"];

const DAY_MS = 24 * 60 * 60 * 1000;

function formatUtcDate(value: Date): string {
  return value.toISOString().slice(0, 10);
}

/**
 * Builds the UTC calendar-day interval used by Analytics. The end boundary is the following
 * midnight and is exclusive, so a completed day contains exactly 24 hourly buckets.
 */
/** Returns the browser timezone used when Analytics renders its native Date values. */
export function getAnalyticsDisplayTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export function getUtcCalendarDayRange(localDate: string): { startDate: Date; endDate: Date } {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(localDate)) {
    throw new Error("Date must use the YYYY-MM-DD format.");
  }

  const startDate = new Date(`${localDate}T00:00:00.000Z`);
  if (!Number.isFinite(startDate.getTime()) || formatUtcDate(startDate) !== localDate) {
    throw new Error("Date is invalid.");
  }

  return { startDate, endDate: new Date(startDate.getTime() + DAY_MS) };
}

/**
 * Calculates start and end dates for a preset date range.
 * Returns the dates that should be set when a preset is selected.
 */
export function getDateRangeForPreset(
  preset: DateRangePreset,
  now: Date = new Date()
): { startDate: Date; endDate: Date } {
  const endDate = new Date(now);
  const today = getUtcCalendarDayRange(formatUtcDate(now));

  switch (preset) {
    case "today":
      return { startDate: today.startDate, endDate };
    case "yesterday": {
      const yesterday = getUtcCalendarDayRange(
        formatUtcDate(new Date(today.startDate.getTime() - DAY_MS))
      );
      return yesterday;
    }
    case "last-7-days":
      return { startDate: new Date(endDate.getTime() - 7 * DAY_MS), endDate };
    case "last-30-days":
      return { startDate: new Date(endDate.getTime() - 30 * DAY_MS), endDate };
    case "last-90-days":
      return { startDate: new Date(endDate.getTime() - 90 * DAY_MS), endDate };
    case "custom":
      // Return current dates unchanged for custom
      return { startDate: endDate, endDate };
  }
}

/** Default Analytics filter selection: today (start of day to now) across every area. */
export const DEFAULT_ANALYTICS_FILTERS: AnalyticsFilters = {
  ...getDateRangeForPreset("today"),
  areaId: ALL_AREAS_FILTER_VALUE,
};

/** Returns the display label for an area filter selection, e.g. "all-areas" -> "All Areas". */
export function getAnalyticsAreaLabel(
  areaId: AnalyticsFilters["areaId"],
  areas: AreaLocation[]
): string {
  if (areaId === ALL_AREAS_FILTER_VALUE) return "All Areas";
  return areas.find((area) => area.id === areaId)?.name ?? areaId;
}
